import React, { useState, useEffect } from 'react';
import { 
  Box, Typography, Button, Paper, Table, TableBody, TableCell, 
  TableContainer, TableHead, TableRow, Chip, CircularProgress, IconButton, Tooltip
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import { useNavigate } from 'react-router-dom';
import superAdminService from '../services/superAdminService';

const SuperAdminQuotes = () => {
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [prices, setPrices] = useState({}); // Store fetched prices

  useEffect(() => {
    const fetchQuotes = async () => {
      try {
        const data = await superAdminService.getAllQuotes();
        setQuotes(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Failed to load quotes", error);
      } finally {
        setLoading(false);
      }
    };
    fetchQuotes();
  }, []);

  // Function to fetch price on demand (lazy load)
  const handleCheckPrice = async (id) => {
      const data = await superAdminService.getQuotePrice(id);
      if (data && data.totalPrice) {
          setPrices(prev => ({...prev, [id]: data.totalPrice}));
      } else {
          setPrices(prev => ({...prev, [id]: 'Error'}));
      }
  };

  if (loading) return <CircularProgress sx={{ m: 5 }} />;

  return (
    <Box sx={{ p: 3 }}>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/super-admin/dashboard')} sx={{ mb: 3 }}>
        Back to Dashboard
      </Button>
      
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 'bold' }}>
        Global Quote Pipeline
      </Typography>

      <TableContainer component={Paper}>
        <Table>
          <TableHead sx={{ bgcolor: '#f5f5f5' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Dealer</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Customer</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Product</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Value (£)</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {quotes.map((quote) => (
              <TableRow key={quote.id} hover>
                <TableCell>
                    {new Date(quote.createdAt).toLocaleDateString()}
                    <Typography variant="caption" display="block" color="text.secondary">
                        ID: {quote.id}
                    </Typography>
                </TableCell>
                
                <TableCell>
                    <Typography fontWeight="bold">{quote.dealerName}</Typography>
                    <Typography variant="caption" color="text.secondary">{quote.dealerBrand}</Typography>
                </TableCell>

                <TableCell>
                    {quote.customerName}
                </TableCell>

                <TableCell>
                    {quote.productSummary}
                    <Typography variant="caption" display="block" color="text.secondary">
                        {quote.dimensions}
                    </Typography>
                </TableCell>

                <TableCell>
                    <Chip 
                        label={quote.status || 'PENDING'} 
                        size="small" 
                        color={quote.status === 'COMPLETE' ? 'success' : 'default'} 
                    />
                </TableCell>

                <TableCell>
                    {prices[quote.id] ? (
                        <Typography fontWeight="bold" color="success.main">
                            £{prices[quote.id].toLocaleString()}
                        </Typography>
                    ) : (
                        <Tooltip title="Calculate Price">
                            <IconButton 
                                size="small" 
                                onClick={() => handleCheckPrice(quote.id)}
                                sx={{ bgcolor: '#f0fdf4', color: '#16a34a' }}
                            >
                                <AttachMoneyIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default SuperAdminQuotes;